import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  AlertBanner,
  Box,
  Button,
  ButtonSelect,
  Icon,
  Menu,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@saviynt/design-system';

import { useLocalization } from '../../../../hooks';
import { useTypedSelector } from '../../../../hooks/use-typedselector';
import { HEADER_MAPPING } from '../../../../utils';
import useDiscoveryFileData from '../../data-layer/transformingPreviewTableData';
import {
  type Column,
  type ColumnOption,
  type Row,
} from '../../data-layer/types';
import {
  setCriticalCount,
  setCriticalMessage,
  setUpdatedHeaders,
} from '../../data-store/DiscoveryFileUploadSlice';

import './FileUploadPreviewTableContent.css';

interface MenuHandle {
  closeDropdown: () => void;
  openDropdown?: () => void;
}

/**
 * File Upload Preview table content after successful file upload
 */
function FileUploadPreviewTableContent() {
  const {
    columnOptions,
    columns: initialColumns,
    rows,
  } = useDiscoveryFileData();

  const dispatch = useDispatch();
  const formatMessage = useLocalization();

  const [alertVisible, setAlertVisible] = useState(true);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [scrollResetSignal, setScrollResetSignal] = useState(0);

  useEffect(() => {
    const node = scrollContainerRef.current;

    if (!node) {
      return;
    }

    const timeout = setTimeout(() => {
      node.scrollTo({ left: 0, behavior: 'smooth' });
    }, 250);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeout);
  }, [scrollResetSignal]);

  const [columns, setColumns] = useState<Column[]>(
    initialColumns.map((col, i) => ({
      ...col,
      positionKey: col.value,
      index: i + 1,
    }))
  );

  const [excludedColumns, setExcludedColumns] = useState<string[]>([]);

  const isColumnExcluded = useCallback(
    (key: string) => excludedColumns.includes(key),
    [excludedColumns]
  );

  const criticalColumnsCount = useMemo(
    () =>
      columns.filter(
        (col) => col.isCritical && !isColumnExcluded(col.positionKey)
      ).length,
    [columns, isColumnExcluded]
  );

  const criticalMessage = useTypedSelector(
    (state) => state.discoveryFileUpload.criticalMessage
  );

  useEffect(() => {
    dispatch(setCriticalCount(criticalColumnsCount));
  }, [criticalColumnsCount, dispatch]);

  useEffect(() => {
    if (criticalColumnsCount === 0) {
      const headerMap: Record<number, string | null> = {};

      columns.forEach((col) => {
        const labelKey = col.label?.trim().toUpperCase() ?? '';

        headerMap[col.index] = excludedColumns.includes(col.positionKey)
          ? null
          : HEADER_MAPPING[labelKey] || col.label;
      });

      dispatch(setUpdatedHeaders(headerMap));

      const mappedLabels = columns
        .filter((col) => !excludedColumns.includes(col.positionKey))
        .map((col) => (col.label ?? '').toUpperCase().trim());

      const missingMandatoryFields = columnOptions
        .filter(
          (opt) =>
            opt.isMandatory &&
            !mappedLabels.includes(opt.label.toUpperCase().trim())
        )
        .map((opt) => opt.label);

      if (missingMandatoryFields.length > 0) {
        dispatch(setCriticalCount(missingMandatoryFields.length));
        dispatch(setCriticalMessage(`${missingMandatoryFields.join(', ')}`));
      } else {
        dispatch(setCriticalCount(0));
        dispatch(setCriticalMessage(''));
      }
    } else {
      dispatch(setCriticalMessage(''));
    }
  }, [criticalColumnsCount, columns, excludedColumns, columnOptions, dispatch]);

  const sortedColumns = useMemo(
    () =>
      [...columns].sort((a, b) => {
        const aExcluded = isColumnExcluded(a.positionKey);
        const bExcluded = isColumnExcluded(b.positionKey);

        if (aExcluded && !bExcluded) {
          return 1;
        }

        if (!aExcluded && bExcluded) {
          return -1;
        }

        if (a.isCritical === b.isCritical) {
          return 0;
        }

        return a.isCritical ? -1 : 1;
      }),
    [columns, isColumnExcluded]
  );

  const handleChange = (key: string, option: ColumnOption) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.positionKey === key
          ? { ...col, label: option.label, isCritical: option.isCritical }
          : col
      )
    );
    setScrollResetSignal((v) => v + 1);
  };

  const handleExcludeColumn = (key: string) => {
    setExcludedColumns((prev) => [...prev, key]);
    setColumns((prev) =>
      prev.map((col) =>
        col.positionKey === key ? { ...col, isCritical: false } : col
      )
    );
    setScrollResetSignal((v) => v + 1);
  };

  const menuOptionsMap = useMemo(() => {
    const map: Record<string, ColumnOption[]> = {};

    columns.forEach((col) => {
      const selectedLabels = columns
        .filter(
          (c) =>
            !excludedColumns.includes(c.positionKey) &&
            c.positionKey !== col.positionKey &&
            c.label
        )
        .map((c) => c.label);

      const options = columnOptions
        .map((opt) => {
          const def = initialColumns.find((c) => c.value === opt.value);
          const isChecked = opt.value === col.value;
          const isDisabled =
            selectedLabels.includes(opt.label || '') && opt.label !== col.label;

          return {
            ...opt,
            label: opt.label || '',
            isChecked,
            isDisabled,
            isCritical: def?.isCritical ?? false,
          };
        })
        .sort((a, b) => {
          if (a.isDisabled === b.isDisabled) {
            return 0;
          }

          return a.isDisabled ? 1 : -1;
        });

      map[col.positionKey] = options;
    });

    return map;
  }, [columns, excludedColumns, columnOptions, initialColumns]);

  const menuRefs = useRef<Record<string, MenuHandle | null>>({});

  return (
    <Box className='AOB-ModalIsland-body'>
      <Box className='AOB-ModalIsland-body-title'>
        <Typography kind='h3'>
          {formatMessage(
            'INTEGRATIONS.FILEUPLOAD.PREVIEW.TITLE',
            'Here are the first 10 records from the XLSX file uploaded.'
          )}
        </Typography>
      </Box>

      <AlertBanner
        colorTheme='Critical'
        title={
          criticalColumnsCount === 0 && criticalMessage.length > 0
            ? formatMessage(
                'INTEGRATIONS.FILEUPLOAD.PREVIEW.ALERT.MISSING_HEADER',
                'Mandatory Header Missing'
              )
            : `${criticalColumnsCount} ${formatMessage(
                'INTEGRATIONS.FILEUPLOAD.PREVIEW.ALERT.COLUMN_ERRORS_SUFFIX',
                'Column Errors Detected'
              )}`
        }
        description={
          criticalMessage ||
          formatMessage(
            'INTEGRATIONS.FILEUPLOAD.PREVIEW.ALERT.FIX_ISSUES',
            'Please fix the issues before proceeding to data import.'
          )
        }
        isVisible={
          alertVisible &&
          (criticalColumnsCount > 0 || criticalMessage.length > 0)
        }
        onCancel={() => setAlertVisible(false)}
        className='AOB-ModalIsland-body-alert'
      />

      <Box className='AOB-ModalIsland-body-table'>
        <div className='AOB-ImportDiscoveryTable-Body' ref={scrollContainerRef}>
          <Table>
            <TableBody>
              <TableRow>
                {sortedColumns.map((column) => {
                  const isExcluded = isColumnExcluded(column.positionKey);

                  return (
                    <TableCell
                      key={column.positionKey}
                      className='AOB-ImportDiscoveryTable-fixedCell'>
                      <Menu
                        kind='simple'
                        options={menuOptionsMap[column.positionKey]}
                        ref={(el) => {
                          menuRefs.current[column.positionKey] =
                            el as MenuHandle | null;
                        }}
                        trigger={
                          <ButtonSelect
                            kind='rectangle'
                            label={
                              isExcluded ? (
                                ''
                              ) : (
                                <Typography kind='body3'>
                                  {column.label}
                                </Typography>
                              )
                            }
                            size='small'
                            prefixIcon={
                              column.isCritical ? (
                                <Icon kind='AlertWarningFilled' />
                              ) : undefined
                            }
                            className={
                              column.isCritical
                                ? 'AOB-ImportDiscoveryTable-menu-critical'
                                : undefined
                            }
                            dataTestId='aob-fileupload-previewtable-column'
                          />
                        }
                        onChange={(selected) => {
                          handleChange(
                            column.positionKey,
                            selected as ColumnOption
                          );
                        }}
                        isPlacementLocked
                        dropdownClassName='AOB-ImportDiscoveryTable-menu-dropdown'
                        footerComp={
                          <Button
                            size='small'
                            kind='ghost'
                            className='AOB-ImportDiscoveryTable-menu-footer'
                            onClick={() => {
                              handleExcludeColumn(column.positionKey);
                              menuRefs.current[
                                column.positionKey
                              ]?.closeDropdown();
                            }}
                            dataTestId='aob-fileupload-previewtable-excludefromimport'>
                            <span className='AOB-ImportDiscoveryTable-footer-btn-content'>
                              <Icon kind='Delete' size='small' />
                              <Typography
                                kind='body3'
                                color='--color-foreground-primary-medium'>
                                {formatMessage(
                                  'INTEGRATIONS.FILEUPLOAD.PREVIEW.EXCLUDE',
                                  'Exclude from import'
                                )}
                              </Typography>
                            </span>
                          </Button>
                        }
                      />
                    </TableCell>
                  );
                })}
              </TableRow>

              {rows.map((row: Row) => (
                <TableRow key={row.AssetID}>
                  {sortedColumns.map((column) => {
                    const isExcluded = isColumnExcluded(column.positionKey);
                    const isFirst = column.value === initialColumns[0].value;

                    return (
                      <TableCell key={`${row.AssetID}-${column.positionKey}`}>
                        <Typography
                          kind={isFirst ? 'body2-bold' : 'body2'}
                          lineLimit={2}
                          color={
                            isExcluded
                              ? '--color-foreground-neutral-disabled'
                              : '--color-foreground-secondary-bold'
                          }
                          className='AOB-ImportDiscoveryTable-RowData'>
                          {row[column.value]}
                        </Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Box>
    </Box>
  );
}

export default FileUploadPreviewTableContent;
